/**
 * LA BIBLIOTHEQUE: on y choisit une bande dessinee, et on la lit.
 *
 * DEUX ECRANS, UN SEUL PANNEAU, comme au musee: le rayon (ce qu'il y a a lire)
 * et la planche (ce qu'on lit). Le joueur passe de l'un a l'autre sans que rien
 * ne se ferme sous ses yeux, et la fermeture, la barriere a raccourcis et le
 * fond sont ceux de tous les autres panneaux.
 *
 * LES CASES SONT TRACEES PAR LA MEME FONCTION QUE LES DESSINS DU MUSEE
 * (`tracerDessin`). Ce n'est pas une economie de lignes, c'est une garantie:
 * les BD et les dessins des joueurs sont le meme format (`Trait`, grille de 0 a
 * 1000), donc une seule fonction sait les tracer, et celle qui ne sert pas tous
 * les jours ne peut pas pourrir dans son coin.
 *
 * AUCUN CHAMP DE SAISIE ici: on ne fait que lire. C'est un pave de boutons, la
 * regle du panneau de l'ecole.
 */
import { Panneau, bouton, el } from './commun.ts'
import { tracerDessin } from './musee.ts'
import { BANDES_DESSINEES, type BandeDessinee } from '../../shared/data/bd.ts'
import { journal } from '../log/journal.ts'

/** Cote d'une case, en pixels de canvas. La CSS la remet a l'echelle. */
const COTE_CASE = 400

export class PanneauBibliotheque extends Panneau {
  constructor(parent: HTMLElement) {
    super(parent, 'bibliotheque', 'Bibliothèque', '📚', 'panneau-fond--large panneau-fond--bibliotheque')
  }

  ouvrir(): void {
    journal.input('bibliotheque', 'panneau ouvert')
    this.montrerRayon()
    this.afficher('rayon')
  }

  // ---------------------------------------------------------------- le rayon

  private montrerRayon(): void {
    this.majEntete('Bibliothèque', '📚')
    this.viderCorps()
    this.viderPied()
    this.corps.append(
      el('p', 'musee__attente', 'Choisis une bande dessinée. Elles sont courtes : quatre cases chacune.'),
    )
    const liste = el('div', 'bd__liste')
    for (const bd of BANDES_DESSINEES) liste.append(this.couverture(bd))
    this.corps.append(liste)
  }

  private couverture(bd: BandeDessinee): HTMLElement {
    const carte = el('button', 'galerie__carte')
    carte.type = 'button'
    // La couverture est la PREMIERE case: elle dit vraiment de quoi il s'agit,
    // la ou un pictogramme seul ne raconte rien.
    const premiere = bd.cases[0]
    if (premiere) carte.append(this.toileDe(premiere.traits))
    carte.append(el('span', 'galerie__legende', `${bd.icone} ${bd.titre}`))
    carte.append(el('span', 'bd__numero', bd.resume))
    carte.addEventListener('click', () => this.lire(bd))
    return carte
  }

  private toileDe(traits: BandeDessinee['cases'][number]['traits']): HTMLCanvasElement {
    const toile = el('canvas', 'bd__case-toile')
    toile.width = COTE_CASE
    toile.height = COTE_CASE
    const ctx = toile.getContext('2d')
    if (ctx) {
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, COTE_CASE, COTE_CASE)
      // `tracerDessin` ajuste la grille de COTE_DESSIN au cote demande: c'est
      // pour ca que les cases sont ecrites en coordonnees normalisees.
      tracerDessin(ctx, traits, COTE_CASE)
    }
    return toile
  }

  // -------------------------------------------------------------- la planche

  /**
   * Affiche la planche entiere, toutes ses cases a la suite.
   *
   * PAS DE PAGE A PAGE. Une case a la fois demanderait un bouton « suivant »
   * qu'un lecteur debutant doit trouver a chaque phrase, et on perdrait le
   * geste le plus naturel sur un telephone: faire defiler. Quatre cases dans un
   * panneau defilant se lisent d'un trait.
   */
  private lire(bd: BandeDessinee): void {
    journal.input('bibliotheque', `lit « ${bd.titre} »`, { id: bd.id, cases: bd.cases.length })
    this.majEntete(bd.titre, bd.icone)
    this.viderCorps()
    this.viderPied()

    const planche = el('div', 'bd__planche')
    for (const [i, c] of bd.cases.entries()) {
      const boite = el('div', 'bd__case')
      boite.append(el('span', 'bd__numero', `${i + 1} / ${bd.cases.length}`))
      boite.append(this.toileDe(c.traits))
      boite.append(el('p', 'bd__texte', c.texte))
      planche.append(boite)
    }
    this.corps.append(planche)
    this.corps.append(el('p', 'musee__signature', `— ${bd.auteur}`))
    this.pied.append(bouton('← Le rayon', () => this.montrerRayon()))
    // On revient en haut: sans ca, on ouvrait la BD au milieu, la ou le rayon
    // avait ete laisse.
    this.corps.scrollTop = 0
  }
}
